import React, { useEffect, useState } from 'react';
import ReusableTable, { TableColumn, TableAction } from '../components/adminTable';
import Pagination from '../components/Pagination';
import Drawer from '../../sharedComponents/Drawer';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';
import { Eye, MoreVertical } from 'lucide-react';
import { useNotification } from '../../sharedComponents/Notifications';
import {
    manuscriptReviewByDeskService,
    categoryService,
    ManuscriptReviewByDeskItem,
    Category,
    GetManuscriptReviewByDeskPayload,
} from '../services/adminAuth';

const decisionLabel = (d?: string | null) => {
    switch (d) {
        case 'AcceptableForPublication':     return 'Acceptable for Publication';
        case 'NotAcceptableForPublication':  return 'Not Acceptable for Publication';
        case 'AcceptableForFunding':         return 'Acceptable for Funding';
        case 'NotAcceptableForFunding':      return 'Not Acceptable for Funding';
        case 'ReturnToReviewer':             return 'Return to Reviewer for Re-Assessment';
        case 'ReturnToAuthorForRevisedCopy': return 'Return to Author for Revised Copy';
        default:                             return d ?? '—';
    }
};

// Matches the badge style used in PrimaFacieReports + ReviewerEvaluationReports:
// pill, no border, semibold text on a tinted surface.
const decisionBadgeClasses = (d?: string | null) => {
    if (d === 'AcceptableForPublication' || d === 'AcceptableForFunding')
        return 'bg-emerald-50 text-emerald-700';
    if (d === 'NotAcceptableForPublication' || d === 'NotAcceptableForFunding')
        return 'bg-red-50 text-red-700';
    if (d === 'ReturnToReviewer' || d === 'ReturnToAuthorForRevisedCopy')
        return 'bg-amber-50 text-amber-700';
    return 'bg-gray-50 text-gray-700';
};

const AllReviewedManuscripts: React.FC = () => {
    const { showNotification } = useNotification();

    const [items, setItems] = useState<ManuscriptReviewByDeskItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [filterCategory, setFilterCategory] = useState<number>(0);
    const [categories, setCategories] = useState<Category[]>([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<ManuscriptReviewByDeskItem | null>(null);

    // ── Fetchers ─────────────────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await categoryService.getAllCategories(true);
                if (!cancelled && res.isSuccess && res.data) setCategories(res.data);
            } catch { /* fail soft */ }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const handler = setTimeout(async () => {
            setLoading(true);
            try {
                const payload: GetManuscriptReviewByDeskPayload = {
                    pageNumber: currentPage,
                    pageSize,
                    sortBy: '',
                    sortDescending: true,
                    search,
                    categoryId: filterCategory,
                };
                const res = await manuscriptReviewByDeskService.getManuscriptAllReviewByDesk(payload);
                if (cancelled) return;
                if (res.isSuccess && res.data) {
                    setItems(res.data.data);
                    setTotalRecords(res.data.totalRecords);
                    setTotalPages(res.data.totalPages);
                } else {
                    showNotification('error', {
                        title: 'Error',
                        message: res.message || 'Failed to load reviewed manuscripts',
                    });
                }
            } catch {
                if (cancelled) return;
                showNotification('error', {
                    title: 'Error',
                    message: 'An error occurred while loading reviewed manuscripts',
                });
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 350);

        return () => {
            cancelled = true;
            clearTimeout(handler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, search, filterCategory]);

    // ── Action ───────────────────────────────────────────────────────────────

    const handleViewDetails = (row: ManuscriptReviewByDeskItem) => {
        setSelectedRow(row);
        setIsDetailsOpen(true);
    };

    // ── Columns ──────────────────────────────────────────────────────────────

    const columns: TableColumn<ManuscriptReviewByDeskItem>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900">{value || '—'}</span>,
        },
        {
            key: 'title',
            label: 'Title',
            className: 'max-w-[260px]',
            render: (value: string) => (
                <div className="font-medium text-gray-700 leading-snug truncate" title={value}>
                    {value || '—'}
                </div>
            ),
        },
        {
            key: 'authorName',
            label: 'Author',
            render: (value: string) => <span className="text-sm text-gray-700">{value || '—'}</span>,
        },
        {
            key: 'reviewerName',
            label: 'Reviewer',
            render: (value: string) => <span className="text-sm text-gray-700">{value || '—'}</span>,
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value: string) => <span className="text-sm text-gray-600">{value || '—'}</span>,
        },
        {
            key: 'decision',
            label: 'Decision',
            render: (value: string) => (
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${decisionBadgeClasses(value)}`}>
                    {decisionLabel(value)}
                </span>
            ),
        },
        {
            key: 'totalScore',
            label: 'Score',
            render: (value: number) => (
                <span className="text-sm font-semibold text-gray-700">{value ?? '—'}</span>
            ),
        },
        {
            key: 'currentStage',
            label: 'Current Stage',
            render: (value: string) => (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700">
                    {value || '—'}
                </span>
            ),
        },
    ];

    const actions: TableAction<ManuscriptReviewByDeskItem>[] = [
        {
            icon: <MoreVertical className="w-5 h-5" />,
            onClick: () => undefined,
            title: 'Actions',
            variant: 'dropdown',
            dropdownItems: [
                {
                    label: 'View Details',
                    icon: <Eye className="w-4 h-4 text-blue-600" />,
                    onClick: handleViewDetails,
                    className: 'text-blue-600',
                },
            ],
        },
    ];

    // ── Filter Panel ─────────────────────────────────────────────────────────

    const filterPanel = (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Thematic Area</label>
                    <select
                        value={filterCategory}
                        onChange={(e) => { setFilterCategory(Number(e.target.value)); setCurrentPage(1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#088A61]"
                    >
                        <option value={0}>All thematic areas</option>
                        {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
            </div>
            <button
                onClick={() => { setFilterCategory(0); setCurrentPage(1); }}
                className="mt-4 text-sm text-[#088A61] hover:underline font-medium"
            >
                Clear all filters
            </button>
        </div>
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">All Reviewed Manuscripts</h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Every manuscript review completed across the system.
                </p>
            </div>

            <ReusableTable
                title="Reviewed Manuscripts"
                subTitle={`${totalRecords} record${totalRecords === 1 ? '' : 's'}`}
                columns={columns}
                data={items}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, ID, author, reviewer…"
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No reviewed manuscripts found."
                showSearch
                showFilter
                filterPanel={filterPanel}
                isFilterOpen={isFilterOpen}
                onFilterToggle={() => setIsFilterOpen(o => !o)}
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            <Drawer
                isOpen={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
                title="Reviewed Manuscript Details"
                width="max-w-3xl"
            >
                {selectedRow && (
                    <AdminSubmissionDetails submissionId={selectedRow.submissionId} />
                )}
            </Drawer>
        </div>
    );
};

export default AllReviewedManuscripts;
